/**
 * BLOCK / INSERT rendering — real geometry (not a placeholder rect).
 *
 * Implements AutoCAD-style INSERT rendering:
 *   - Full 2D affine transform via `composeBlockMatrix` (see blockMatrix.ts):
 *     translate(insertion) × rotate × scale(sX, sY) × translate(-basePoint).
 *     Supports negative scale (= mirror) and zero-rotation fast path.
 *   - MINSERT grid: columnCount × rowCount copies spaced by
 *     columnSpacing / rowSpacing in the block's local coordinate system.
 *   - Recursive child rendering via `renderBlockChildEntity` so the
 *     same implementation serves INSERT entities and MLEADER block
 *     content — AutoCAD parity Priority #1.
 *   - BYBLOCK inheritance via `resolveByBlockLayer`: entity with
 *     color="ByBlock" inherits the INSERT's color, explicit values are
 *     preserved, ByLayer is left alone (resolves via layer table).
 *   - Missing block definition → dashed red indicator with block name
 *     (never silently dropped — Priority #1).
 *
 * File size budget: ≤500 lines. Matrix math is split out into
 * blockMatrix.ts so this module stays under the ceiling.
 */

import * as React from "react";

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import { coerceColor } from "@dxf-ai-editor/drawing-model";
import type { BlockDefinition, EntityColor } from "@dxf-ai-editor/drawing-model";
import {
  evaluateBlock,
  buildEvaluateBlockCacheKey,
} from "@dxf-ai-editor/block-evaluator";
import { readNumber, describeArc } from "./renderHelpers";
import { composeBlockMatrix } from "./blockMatrix";
import { resolveNestedBlockChild } from "./blockInstanceResolve";
import { readInstanceOverrides } from "./blockGripCollector";
import {
  decodeAnnotativeContextsJson,
  resolveCannoscaleContext,
} from "./cannoscale/useCannoscaleResolver";
import { renderAttribute } from "./renderAttribute";

export { composeBlockMatrix } from "./blockMatrix";
export type { BlockMatrix } from "./blockMatrix";

/* ── FIN.9 — Block evaluator memoization ─────────────────────── */
/**
 * Module-level cache for `evaluateBlock` results. Keyed by:
 *   `${blockName}|${customProps}|${visibilityState}`
 *
 * The cached entry also stores the BlockDefinition reference; if the
 * caller passes a different reference we invalidate (BlockDefinition is
 * immutable within a document version — the reference changes on every
 * dispatch that touches the block, so identity check is exact).
 *
 * Size cap: 2048 entries. LRU eviction would require a doubly-linked
 * list; for a CAD editor the working set rarely exceeds a few hundred
 * unique (block, param-combination) pairs, so a simple FIFO trim
 * suffices.
 */
interface CachedEval {
  readonly defRef: BlockDefinition;
  readonly entities: BlockDefinition["entities"];
}
const EVAL_CACHE_MAX = 2048;
const evalCache = new Map<string, CachedEval>();

function getCachedEvaluation(
  entity: EntityPreview,
  blockDef: BlockDefinition,
  blockDefinitions: Record<string, BlockDefinition>,
): BlockDefinition["entities"] {
  // Static blocks short-circuit the evaluator; the cache maps them as a
  // pass-through for symmetry but we don't bother computing the key for
  // them — they're a trivial reference return.
  if (!blockDef.dynamicMetadata) {
    return blockDef.entities as BlockDefinition["entities"];
  }
  // Variant D Phase 1 — pull per-INSERT customProperties / visibilityState
  // from the preview's flat `properties` bag via readInstanceOverrides().
  // The `EntityPreview` contract schema only carries `properties:
  // Record<string,string>` so the typed customProperties map is
  // reconstructed from keys with the `Param: ` prefix plus the
  // dedicated `Visibility` key. Without this, the evaluator cache key
  // never sees a DYNSET-induced change and visibility toggles are
  // invisible to the renderer — see e2e/visibility-recompute.spec.ts.
  const overrides = readInstanceOverrides(entity);
  const key = buildEvaluateBlockCacheKey(blockDef.name, overrides);
  const cached = evalCache.get(key);
  if (cached && cached.defRef === blockDef) {
    return cached.entities;
  }
  const result = evaluateBlock({
    blockDef,
    instance: {
      customProperties: overrides.customProperties,
      ...(overrides.visibilityState !== undefined
        ? { visibilityState: overrides.visibilityState }
        : {}),
    },
    blockDefinitions,
    parentChain: [],
  });
  const entities = result.entities as BlockDefinition["entities"];
  if (evalCache.size >= EVAL_CACHE_MAX) {
    // Trim oldest quarter to amortise eviction cost.
    const drop = Math.floor(EVAL_CACHE_MAX / 4);
    let count = 0;
    for (const k of evalCache.keys()) {
      if (count++ >= drop) break;
      evalCache.delete(k);
    }
  }
  evalCache.set(key, { defRef: blockDef, entities });
  return entities;
}

/**
 * Exposed for unit tests / dev tools — clears the entire evaluator
 * cache. Call this from tests that want deterministic measurement of
 * the first-render cost.
 */
export function clearBlockEvalCache(): void {
  evalCache.clear();
}

/* ── BYBLOCK / BYLAYER resolution ─────────────────────────────── */

/**
 * AutoCAD BYBLOCK / BYLAYER inheritance for entities rendered inside an
 * INSERT instance.
 *
 *   - entity.color.kind === "byBlock" → inherit parent.color (the INSERT's)
 *   - entity.linetype === "ByBlock"   → inherit parent.linetype
 *   - entity.lineweight === "ByBlock" → inherit parent.lineweight
 *   - otherwise (explicit values, ByLayer) → untouched
 *
 * ByLayer is intentionally NOT resolved here: it must be read from the
 * Layer table at a higher level. ByBlock is the only value the INSERT
 * itself can fill in.
 */
export function resolveByBlockLayer<
  T extends {
    layer: string;
    // EPIC-COLOR-UNION S1 — `color` is the typed `EntityColor` union; linetype /
    // lineweight stay plain strings (ByBlock keyword strings).
    color: EntityColor;
    linetype: string;
    lineweight: string;
  },
>(
  child: T,
  parent: {
    color: EntityColor;
    linetype: string;
    lineweight: string;
    layer: string;
  },
): T {
  // ByBlock colour inherits the parent INSERT's colour (union compare on the
  // discriminant); every other case keeps the child's own colour.
  //
  // EPIC-COLOR-UNION S1 — `coerceColor`-guard the discriminant: block
  // definitions can still hold a legacy STRING colour at render time (PROD fast
  // paths / older reducer-built data that bypassed the schema preprocess). A raw
  // "ByBlock" string has no `.kind`, so without coercion the child would keep its
  // raw ByBlock value instead of inheriting the parent INSERT colour. For the
  // non-byBlock case we return the ORIGINAL `child.color` reference (not the
  // coerced copy) so the identity short-circuit below still elides a re-render
  // when nothing changed.
  const childColor = coerceColor(child.color);
  const color =
    childColor.kind === "byBlock" ? coerceColor(parent.color) : child.color;
  const linetype =
    child.linetype === "ByBlock" ? parent.linetype : child.linetype;
  const lineweight =
    child.lineweight === "ByBlock" ? parent.lineweight : child.lineweight;

  if (
    color === child.color &&
    linetype === child.linetype &&
    lineweight === child.lineweight
  ) {
    return child;
  }
  return { ...child, color, linetype, lineweight } satisfies T;
}

/* ── Recursive child-entity rendering ─────────────────────────── */

/**
 * Render a single entity that lives inside a block definition. Walks
 * BlockDefinition.entities and emits SVG primitives for every supported
 * type (Line, Circle, Arc, LWPOLYLINE, Text, MText, Block, Attdef).
 *
 * - ATTDEF: substitutes `attrLookup` value (by handle, then tag), falls
 *   back to the ATTDEF's default text. Never blank (Priority #1).
 * - Nested Block: resolves recursively until `remainingDepth` hits 0,
 *   at which point a "(...)" cap marker is drawn so the user knows the
 *   recursion was truncated. `visitedBlocks` additionally tracks the
 *   ancestor chain so A→B→A circular references stop immediately with
 *   a "(↺NAME)" marker rather than blowing the depth cap.
 * - Missing nested block definition: "?<name>" marker, never silently
 *   dropped.
 *
 * Moved here from renderMultileader.tsx so it can be reused by every
 * INSERT renderer in the app, not just MLEADER block content.
 */
export function renderBlockChildEntity(
  child: BlockDefinition["entities"][number],
  attrLookup: Map<string, string>,
  blockDefinitions: Record<string, BlockDefinition>,
  remainingDepth: number,
  keyPrefix: string,
  visitedBlocks?: Set<string>,
  /**
   * PR-11 — active CANNOSCALE name. Threaded through to ATTDEF
   * children via `renderAttribute` so the per-scale `attribute`-kind
   * context can override insertion / rotation / effective height. `null`
   * (legacy default) preserves the pre-PR-11 non-annotative behaviour.
   */
  annotationScaleName?: string | null,
  /**
   * PR-11 — header `ANNOALLVISIBLE` flag. Same semantics as the
   * top-level `renderEntity` — `true` keeps annotative ATTDEFs without
   * a matching context visible (AutoCAD's "show at all scales").
   */
  annoAllVisible: boolean = true,
): React.ReactNode {
  if (child.type === "Line") {
    return (
      <line
        key={`${keyPrefix}-l-${child.id}`}
        x1={child.start.x}
        y1={child.start.y}
        x2={child.end.x}
        y2={child.end.y}
        className="drawing-stage-entity-stroke"
      />
    );
  }
  if (child.type === "Circle") {
    return (
      <circle
        key={`${keyPrefix}-c-${child.id}`}
        cx={child.center.x}
        cy={child.center.y}
        r={child.radius}
        className="drawing-stage-entity-stroke"
        fill="none"
      />
    );
  }
  if (child.type === "Arc") {
    return (
      <path
        key={`${keyPrefix}-a-${child.id}`}
        d={describeArc(
          child.center.x,
          child.center.y,
          child.radius,
          child.startAngle,
          child.endAngle,
        )}
        className="drawing-stage-entity-stroke"
        fill="none"
      />
    );
  }
  if (child.type === "LWPOLYLINE") {
    const pts = child.vertices.map((v) => `${v.x},${v.y}`).join(" ");
    return child.closed ? (
      <polygon
        key={`${keyPrefix}-p-${child.id}`}
        points={pts}
        className="drawing-stage-entity-stroke"
        fill="none"
      />
    ) : (
      <polyline
        key={`${keyPrefix}-p-${child.id}`}
        points={pts}
        className="drawing-stage-entity-stroke"
        fill="none"
      />
    );
  }
  if (child.type === "Text") {
    return (
      <text
        key={`${keyPrefix}-t-${child.id}`}
        x={child.insertionPoint.x}
        y={child.insertionPoint.y}
        fontSize={child.height}
        className="drawing-stage-text-entity"
        transform={
          child.rotation !== 0
            ? `rotate(${-child.rotation} ${child.insertionPoint.x} ${child.insertionPoint.y})`
            : undefined
        }
      >
        {child.contents}
      </text>
    );
  }
  if (child.type === "MText") {
    return (
      <text
        key={`${keyPrefix}-mt-${child.id}`}
        x={child.insertionPoint.x}
        y={child.insertionPoint.y}
        fontSize={child.nominalHeight}
        className="drawing-stage-text-entity"
      >
        {child.contents}
      </text>
    );
  }
  if (child.type === "Attdef") {
    // PR-11: delegate to the dedicated ATTDEF renderer so the
    // annotative `attribute`-kind context (per-scale insertion +
    // rotation, effectiveScale-multiplied height) is honoured. Legacy
    // callers that don't thread `annotationScaleName` get the original
    // master-field render via the resolver's non-annotative fast path.
    return renderAttribute(
      child,
      attrLookup,
      keyPrefix,
      annotationScaleName,
      annoAllVisible,
    );
  }
  if (child.type === "Block") {
    // Lane I (flatten-unify) + codex PR #472 round-3 finding 1 — the nested
    // INSERT's classification (depth-cap / cycle / missing), its
    // `composeBlockMatrix` transform (the nested def's OWN base point, `R·S`
    // order — NOT the pre-Lane-I `translate·scale·rotate` string that dropped
    // the base point and reversed the order), and its per-instance dynamic
    // `evaluateBlock` (DYN-3 Batch C: the child INSERT's own customProperties
    // / visibilityState; a no-op pass-through for static nested blocks) ALL
    // live in the shared `resolveNestedBlockChild`, which the Insert-dialog
    // preview-bounds walker (`blockInstanceResolve.ts::computeRenderedPreviewBounds`)
    // ALSO calls — so the preview frame can never drift from what this renderer
    // draws. A nested INSERT is deliberately NOT MINSERT-grid-expanded even if
    // it carries columnCount/rowCount (MINSERT cannot legally nest inside a
    // block definition — typed `blockFromEntity` + PDF `walkBlock` agree); only
    // the top-level `renderBlockInstance` tiles the grid.
    const res = resolveNestedBlockChild(
      child,
      blockDefinitions,
      remainingDepth,
      visitedBlocks,
    );
    if (res.kind === "depth-cap") {
      return (
        <text
          key={`${keyPrefix}-blk-cap-${child.id}`}
          x={child.insertionPoint.x}
          y={child.insertionPoint.y}
          fontSize={2.5}
          className="drawing-stage-text-entity"
        >
          {`(...${child.blockName})`}
        </text>
      );
    }
    if (res.kind === "cycle") {
      // Cycle detection — the block is already on the ancestor chain; stop
      // recursing and emit a marker (prevents A → B → A infinite loops).
      return (
        <text
          key={`${keyPrefix}-blk-cycle-${child.id}`}
          x={child.insertionPoint.x}
          y={child.insertionPoint.y}
          fontSize={2.5}
          className="drawing-stage-text-entity"
        >
          {`(↺${child.blockName})`}
        </text>
      );
    }
    if (res.kind === "missing") {
      return (
        <text
          key={`${keyPrefix}-blk-missing-${child.id}`}
          x={child.insertionPoint.x}
          y={child.insertionPoint.y}
          fontSize={2.5}
          className="drawing-stage-text-entity"
        >
          {`?${child.blockName}`}
        </text>
      );
    }
    return (
      <g key={`${keyPrefix}-blk-${child.id}`} transform={res.matrix.toSvgTransform()}>
        {res.evaluatedEntities.map((grandChild) =>
          renderBlockChildEntity(
            resolveByBlockLayer(grandChild, child),
            attrLookup,
            blockDefinitions,
            remainingDepth - 1,
            `${keyPrefix}-blk-${child.id}`,
            res.nextVisited,
            annotationScaleName,
            annoAllVisible,
          ),
        )}
      </g>
    );
  }
  return null;
}

/**
 * Render the full content of a block definition to an array of SVG
 * nodes (without any enclosing `<g transform=…>` wrapper — the caller
 * decides how to position the block). Seeds the cycle-detection set
 * with the block's own name so a child that references the same block
 * triggers the `↺` marker immediately.
 *
 * Used by renderArray in renderSimpleEntities.tsx when the associative
 * array points at a source block. Kept separate from
 * `renderBlockInstance` so the caller can reuse the same content at
 * every grid cell (ARRAYRECT/MINSERT) without recomputing it.
 */
export function renderBlockContent(
  blockDef: BlockDefinition,
  blockDefinitions: Record<string, BlockDefinition>,
  options: {
    maxDepth?: number;
    attrLookup?: Map<string, string>;
    /** PR-11 — see renderBlockChildEntity for ANNOALLVISIBLE / scale name semantics. */
    annotationScaleName?: string | null;
    annoAllVisible?: boolean;
  } = {},
): React.ReactNode[] {
  const maxDepth = options.maxDepth ?? 10;
  if (maxDepth <= 0) return [];
  const attrLookup = options.attrLookup ?? new Map<string, string>();
  const annotationScaleName = options.annotationScaleName ?? null;
  const annoAllVisible = options.annoAllVisible ?? true;
  const visited = new Set<string>([blockDef.name]);
  const nodes: React.ReactNode[] = [];
  for (let idx = 0; idx < blockDef.entities.length; idx++) {
    const child = blockDef.entities[idx]!;
    const node = renderBlockChildEntity(
      child,
      attrLookup,
      blockDefinitions,
      maxDepth - 1,
      `${blockDef.name}-${idx}`,
      visited,
      annotationScaleName,
      annoAllVisible,
    );
    if (node != null) nodes.push(node);
  }
  return nodes;
}

/* ── INSERT entity-preview rendering (main entry point) ───────── */

function parseCombinedNumberTuple(
  raw: string | undefined,
  defaults: number[],
): number[] {
  if (!raw) return [...defaults];
  const parts = raw.split(",").map((v) => {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : NaN;
  });
  return defaults.map((def, i) => {
    const parsed = parts[i];
    return parsed != null && Number.isFinite(parsed) ? parsed : def;
  });
}

/**
 * Resolve INSERT geometry from its EntityPreview property bag. Prefers
 * discrete keys (`Insertion X`, `Insertion Y`, `Scale X`, …) when
 * available; falls back to the combined legacy keys (`Insertion point`,
 * `Scale`) emitted by earlier shell writers.
 */
function readInsertGeometry(props: Record<string, string>): {
  blockName: string;
  insertionPoint: { x: number; y: number };
  scaleX: number;
  scaleY: number;
  rotation: number;
  columnCount: number;
  rowCount: number;
  columnSpacing: number;
  rowSpacing: number;
} {
  const blockName = props["Name"] ?? props["Block name"] ?? "";

  let ix = Number(props["Insertion X"]);
  let iy = Number(props["Insertion Y"]);
  if (!Number.isFinite(ix) || !Number.isFinite(iy)) {
    const [px, py] = parseCombinedNumberTuple(props["Insertion point"], [0, 0]);
    if (!Number.isFinite(ix)) ix = px!;
    if (!Number.isFinite(iy)) iy = py!;
  }

  let sx = Number(props["Scale X"]);
  let sy = Number(props["Scale Y"]);
  if (!Number.isFinite(sx) || !Number.isFinite(sy)) {
    const [csx, csy] = parseCombinedNumberTuple(props["Scale"], [1, 1, 1]);
    if (!Number.isFinite(sx)) sx = csx!;
    if (!Number.isFinite(sy)) sy = csy!;
  }
  // Guard against degenerate (zero) scale — collapse to 1.
  if (!Number.isFinite(sx) || Math.abs(sx) < 1e-9) sx = 1;
  if (!Number.isFinite(sy) || Math.abs(sy) < 1e-9) sy = 1;

  return {
    blockName,
    insertionPoint: { x: ix, y: iy },
    scaleX: sx,
    scaleY: sy,
    rotation: readNumber(props, "Rotation", 0),
    columnCount: Math.max(
      1,
      Math.round(
        readNumber(props, "Column count", readNumber(props, "Columns", 1)),
      ),
    ),
    rowCount: Math.max(
      1,
      Math.round(readNumber(props, "Row count", readNumber(props, "Rows", 1))),
    ),
    columnSpacing: readNumber(props, "Column spacing", 0),
    rowSpacing: readNumber(props, "Row spacing", 0),
  };
}

/**
 * Parse the `Attributes` property (when present) into a tag→value
 * lookup. Accepts:
 *   - JSON array `[{"tag":"X","value":"Y"}, ...]` (current shell format)
 *   - Legacy `tag=value;tag2=value2` form
 * Missing / malformed payload → empty map (no throw).
 */
function parseAttributes(raw: string | undefined): Map<string, string> {
  const out = new Map<string, string>();
  if (!raw) return out;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return out;
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (
            item &&
            typeof item === "object" &&
            typeof item.tag === "string" &&
            typeof item.value === "string"
          ) {
            out.set(item.tag, item.value);
          }
        }
      }
    } catch {
      /* fall through to legacy parse */
    }
    return out;
  }
  for (const pair of trimmed.split(";")) {
    const eq = pair.indexOf("=");
    if (eq > 0) {
      out.set(pair.slice(0, eq).trim(), pair.slice(eq + 1).trim());
    }
  }
  return out;
}

/**
 * Render an INSERT entity as real geometry. Handles:
 *   - missing block definition → dashed red indicator with block name
 *   - transform matrix with translate/rotate/scale (negative = mirror)
 *     + block basePoint offset
 *   - MINSERT grid: columnCount × rowCount copies spaced in the block's
 *     local coordinate system
 *   - BYBLOCK inheritance for child entities (via `resolveByBlockLayer`)
 *   - attribute-value substitution for ATTDEF children (via
 *     `Attributes` property)
 *
 * `maxDepth` guards against circular block references (A → B → A) that
 * AutoCAD itself refuses to save but may appear in malformed DXF.
 */
export function renderBlockInstance(opts: {
  entity: EntityPreview;
  blockDefinitions: Record<string, BlockDefinition>;
  maxDepth?: number;
  /**
   * PR-3 — active CANNOSCALE name. When the INSERT carries an
   * annotative `blockref` context for the current scale, the resolver
   * overrides insertion / scaleX / scaleY / rotation. Optional with a
   * `null` default so legacy callers (preview, drag-and-drop) keep
   * working with the master fields only.
   */
  annotationScaleName?: string | null;
  /** PR-3 — see renderEntity for ANNOALLVISIBLE semantics. */
  annoAllVisible?: boolean;
}): React.ReactNode {
  const { entity, blockDefinitions } = opts;
  const annotationScaleName = opts.annotationScaleName ?? null;
  const annoAllVisible = opts.annoAllVisible ?? true;
  const maxDepth = opts.maxDepth ?? 8;
  const props = entity.properties;

  // PR-3 — annotative INSERT visibility / per-scale geometry override.
  // Resolver returns `effectiveScale: 1` and `activeContext: null` for
  // non-annotative INSERTs so the rest of the function behaves as
  // before. When matched, the per-scale insertion / scaleX / scaleY /
  // rotation override the master fields.
  const annoContexts = decodeAnnotativeContextsJson(
    props.__entity_annotative_contexts,
  );
  const annoCtx = resolveCannoscaleContext(
    annoContexts,
    annotationScaleName,
    annoAllVisible,
  );
  if (!annoCtx.isVisible) {
    return null;
  }

  const geomRaw = readInsertGeometry(props);
  // Apply context overrides when the resolver matched a context. The
  // explicit `?? geomRaw.X` fall-throughs keep non-annotative INSERTs
  // untouched.
  const geom = annoCtx.activeContext
    ? {
        ...geomRaw,
        insertionPoint: {
          x: annoCtx.insertion?.x ?? geomRaw.insertionPoint.x,
          y: annoCtx.insertion?.y ?? geomRaw.insertionPoint.y,
        },
        scaleX: annoCtx.scaleX ?? geomRaw.scaleX,
        scaleY: annoCtx.scaleY ?? geomRaw.scaleY,
        rotation: annoCtx.rotation ?? geomRaw.rotation,
      }
    : geomRaw;
  const blockDef = blockDefinitions[geom.blockName];

  if (!blockDef) {
    return (
      <g
        transform={`translate(${geom.insertionPoint.x} ${geom.insertionPoint.y})`}
        data-entity-id={entity.id}
        data-entity-type={entity.type}
        data-block-missing={geom.blockName || "(unnamed)"}
      >
        <rect
          x={-10}
          y={-10}
          width={20}
          height={20}
          fill="none"
          stroke="red"
          strokeDasharray="2 2"
        />
        <text x={0} y={0} fontSize={3} textAnchor="middle" fill="red">
          {`? ${geom.blockName || "(unnamed)"}`}
        </text>
      </g>
    );
  }

  // ── XREF status placeholders (Phase FIN.3) ─────────────────
  // not-found → red dashed rect with the xref path so the user can
  // decide whether to XRELOAD with a fresh path or XDETACH.
  // unloaded  → grey rect to communicate the xref is intentionally
  // suppressed (XRELOAD restores it).
  if (blockDef.xrefStatus === "not-found") {
    return (
      <g
        transform={`translate(${geom.insertionPoint.x} ${geom.insertionPoint.y})`}
        data-entity-id={entity.id}
        data-entity-type={entity.type}
        data-xref-status="not-found"
        data-xref-path={blockDef.xrefPathName}
      >
        <rect
          x={-12}
          y={-12}
          width={24}
          height={24}
          fill="none"
          stroke="red"
          strokeDasharray="2 2"
        />
        <text x={0} y={-2} fontSize={2.5} textAnchor="middle" fill="red">
          {`! ${blockDef.name}`}
        </text>
        <text x={0} y={3} fontSize={2} textAnchor="middle" fill="red">
          {blockDef.xrefPathName || "(no path)"}
        </text>
      </g>
    );
  }
  if (blockDef.xrefStatus === "unloaded") {
    return (
      <g
        transform={`translate(${geom.insertionPoint.x} ${geom.insertionPoint.y})`}
        data-entity-id={entity.id}
        data-entity-type={entity.type}
        data-xref-status="unloaded"
        data-xref-name={blockDef.name}
      >
        <rect
          x={-12}
          y={-12}
          width={24}
          height={24}
          fill="#eeeeee"
          stroke="#999999"
          strokeDasharray="2 2"
        />
        <text x={0} y={0} fontSize={2.5} textAnchor="middle" fill="#666666">
          {`${blockDef.name} (unloaded)`}
        </text>
      </g>
    );
  }

  const baseMatrix = composeBlockMatrix({
    translate: geom.insertionPoint,
    rotateDeg: geom.rotation,
    scaleX: geom.scaleX,
    scaleY: geom.scaleY,
    basePoint: blockDef.basePoint,
  });

  const attrLookup = parseAttributes(props["Attributes"]);

  // Parent-level style that children inherit via ByBlock.
  const parentStyle = {
    // EPIC-COLOR-UNION S1 — the MINSERT parent style colour comes from the
    // INSERT preview's `Color` property string; coerce it to the typed union so
    // `resolveByBlockLayer`'s union ByBlock compare works.
    color: coerceColor(props["Color"] ?? "ByLayer"),
    linetype: props["Linetype"] ?? "ByLayer",
    lineweight: props["Lineweight"] ?? "ByLayer",
    layer: props["Layer"] ?? "0",
  };

  // Seed cycle detection with the root block's own name so a child
  // entity that references the same block triggers the `↺` marker.
  const rootVisited = new Set<string>([blockDef.name]);

  // Dynamic-block evaluation (DYN-1.5): for blocks that carry
  // `dynamicMetadata`, resolve parameter-driven transformations +
  // visibility filtering BEFORE rendering. Static blocks pass through
  // unchanged — the evaluator's no-op fast path returns
  // blockDef.entities by reference so there's zero behavioral drift for
  // pre-DYN-1 content.
  //
  // FIN.9 — wrap the evaluator in a module-level memo keyed on (block,
  // params, visibility). With hundreds of INSERTs sharing a few unique
  // parameter combinations, this collapses evaluateBlock() into an O(1)
  // Map lookup for 99% of calls.
  const resolvedEntities = getCachedEvaluation(
    entity,
    blockDef,
    blockDefinitions,
  );

  const copies: React.ReactNode[] = [];
  for (let row = 0; row < geom.rowCount; row++) {
    for (let col = 0; col < geom.columnCount; col++) {
      const cellMatrix = baseMatrix.translate({
        x: col * geom.columnSpacing,
        y: row * geom.rowSpacing,
      });
      const cellKey = `r${row}c${col}`;
      copies.push(
        <g
          key={cellKey}
          transform={cellMatrix.toSvgTransform()}
          data-entity-id={entity.id}
          data-entity-type={entity.type}
          data-minsert-cell={cellKey}
        >
          {resolvedEntities.map((child, idx) =>
            renderBlockChildEntity(
              resolveByBlockLayer(child, parentStyle),
              attrLookup,
              blockDefinitions,
              maxDepth,
              `${entity.id}-${cellKey}-${idx}`,
              rootVisited,
              annotationScaleName,
              annoAllVisible,
            ),
          )}
        </g>,
      );
    }
  }

  return (
    <g data-entity-id={entity.id} data-entity-type={entity.type}>
      {copies}
    </g>
  );
}
